/**
 * §6.4 Conversation control — barge-in, response speed as the three-preset
 * segmented control (Snappy / Balanced / Patient → 200/350/600ms), call
 * limits, silence handling, and hang-up phrases.
 *
 * Response speed drives Standard's VAD stop_secs and Gemini's server-side
 * silence_duration_ms. OpenAI realtime has no equivalent — it picks a
 * turn-detection mode in the Voice section instead — so the control is
 * disabled there with a tooltip.
 *
 * "Ignore short interjections" is Standard-only for the same
 * declare-what-you-honour reason: on the speech-to-speech engines a word
 * threshold cannot be applied without disabling barge-in outright, so
 * pipeline.py installs no strategy there (ADR 0013) and the control says so.
 */

import { Slider } from "../Slider";
import { cx, formatDuration } from "../../lib/format";
import type { AgentConfig } from "../../lib/types";
import { ENDPOINTING_PRESETS } from "./constants";
import { StringListEditor } from "./StringListEditor";
import { ToggleRow } from "./ToggleRow";
import { Tooltip } from "./Tooltip";
import type { DraftErrors } from "./validation";

export interface ConversationSectionProps {
  config: AgentConfig;
  patch: (partial: Partial<AgentConfig>) => void;
  errors: DraftErrors;
}

export function ConversationSection({ config, patch, errors }: ConversationSectionProps) {
  // Named for what it means, not for one engine: Gemini honours this too, via
  // GeminiVADParams.silence_duration_ms (see runtime/engines/gemini.py).
  const endpointingApplies = config.voice_engine !== "realtime";
  // Only the cascaded engine gets a MinWordsInterruptionStrategy — the
  // speech-to-speech engines can't evaluate one before the caller's audio has
  // already been answered (ADR 0013).
  const minWordsApplies = config.voice_engine === "cascaded";
  const matchesPreset = ENDPOINTING_PRESETS.some((p) => p.ms === config.endpointing_ms);
  const activePreset = ENDPOINTING_PRESETS.find((p) => p.ms === config.endpointing_ms);
  // Instant acknowledgments are synthesized in the agent's Gemini voice, so
  // only that engine can offer them (runtime: engines declare filler_voice).
  const fillerApplies = config.voice_engine === "gemini";

  return (
    <div className="flex flex-col gap-5">
      <ToggleRow
        label="Callers can interrupt"
        help="The agent stops talking when the caller starts."
        checked={config.allow_interruptions}
        onChange={(allow_interruptions) => patch({ allow_interruptions })}
      />

      <Tooltip
        content="Natural (Gemini) only — the acknowledgment clips are synthesized in the agent's own Gemini voice."
        block
        disabled={fillerApplies}
      >
        <div className={cx(!fillerApplies && "opacity-60")}>
          <ToggleRow
            label="Instant acknowledgments"
            help={
              'A short, context-matched "Sure…" or "Let me see…" in the agent\'s own voice while it composes the full reply — the response feels near-instant.'
            }
            checked={config.filler_mode === "contextual"}
            disabled={!fillerApplies}
            onChange={(on) => patch({ filler_mode: on ? "contextual" : "off" })}
          />
        </div>
      </Tooltip>

      <Tooltip
        content="Standard only. Realtime and Gemini transcribe on their own connection, which returns too late to hold a reply back — applying a word threshold there would stop barge-in working at all."
        block
        disabled={minWordsApplies}
      >
        <div className={cx("flex flex-col gap-1.5", !minWordsApplies && "opacity-60")}>
          <span className="text-xs font-medium text-body">Ignore short interjections</span>
          <Slider
            value={config.interruption_min_words}
            onChange={(v) => patch({ interruption_min_words: v })}
            min={1}
            max={5}
            disabled={!config.allow_interruptions || !minWordsApplies}
            label="Minimum words to count as an interruption"
            format={(v) => `${v} word${v === 1 ? "" : "s"}`}
          />
          <p className="text-xs text-muted">
            {minWordsApplies
              ? '"Uh-huh" and "yeah" won\'t stop the agent — it takes at least this many words.'
              : "Not used on this engine — any speech from the caller stops the agent."}
          </p>
          {errors.interruption_min_words && (
            <p className="text-xs text-danger" role="alert">
              {errors.interruption_min_words}
            </p>
          )}
        </div>
      </Tooltip>

      {/* Response speed — endpointing presets (Standard + Gemini) */}
      <Tooltip
        content="Not used on Realtime — it has its own turn-detection mode, set in the Voice section."
        block
        disabled={endpointingApplies}
      >
        <div className={cx("flex flex-col gap-1.5", !endpointingApplies && "opacity-60")}>
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-medium text-body">Response speed</span>
            {!matchesPreset && (
              <span className="font-mono text-xs text-muted">custom · {config.endpointing_ms}ms</span>
            )}
          </div>
          <div role="radiogroup" aria-label="Response speed" className="grid grid-cols-3 gap-2">
            {ENDPOINTING_PRESETS.map((preset) => {
              const selected = preset.ms === config.endpointing_ms;
              return (
                <button
                  key={preset.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={!endpointingApplies}
                  onClick={() => patch({ endpointing_ms: preset.ms })}
                  className={cx(
                    "flex flex-col items-start gap-0.5 rounded border px-3 py-2 text-left transition-colors",
                    selected ? "border-bright bg-surface-raised" : "border-line hover:border-muted",
                    "disabled:cursor-not-allowed",
                  )}
                >
                  <span className={cx("text-sm", selected ? "text-bright" : "text-body")}>
                    {preset.label}
                  </span>
                  <span className="font-mono text-xs text-muted">{preset.ms}ms</span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted">
            {activePreset?.help ?? "How long the agent waits after you stop speaking before replying."}
          </p>
          {errors.endpointing_ms && endpointingApplies && (
            <p className="text-xs text-danger" role="alert">
              {errors.endpointing_ms}
            </p>
          )}
        </div>
      </Tooltip>

      <div className="grid gap-4 border-t border-line pt-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-body">Call time limit</span>
          <Slider
            value={config.max_call_duration_s}
            onChange={(v) => patch({ max_call_duration_s: v })}
            min={30}
            max={3600}
            step={30}
            label="Maximum call duration"
            format={(v) => formatDuration(v)}
          />
          <p className="text-xs text-muted">Hard stop — keeps a stuck call from running up cost.</p>
          {errors.max_call_duration_s && (
            <p className="text-xs text-danger" role="alert">
              {errors.max_call_duration_s}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-body">Silence timeout</span>
          <Slider
            value={config.silence_timeout_s}
            onChange={(v) => patch({ silence_timeout_s: v })}
            min={3}
            max={60}
            label="Silence timeout in seconds"
            format={(v) => `${v}s`}
          />
          <p className="text-xs text-muted">
            After this much silence the agent checks in; after a second strike it hangs up.
          </p>
          {errors.silence_timeout_s && (
            <p className="text-xs text-danger" role="alert">
              {errors.silence_timeout_s}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-body">Hang-up phrases</span>
        <p className="text-xs text-muted">
          When the caller says one of these, the agent wraps up and ends the call.
        </p>
        <StringListEditor
          value={config.end_call_phrases}
          onChange={(end_call_phrases) => patch({ end_call_phrases })}
          placeholder="e.g. goodbye"
          addLabel="Add phrase"
          itemLabel="Hang-up phrase"
          mono
        />
      </div>
    </div>
  );
}
